import { useState, useEffect } from 'react';
import { getSessionInterval } from '../utils/sessionTime';
import LazyImage from './LazyImage';
import { useCategories } from '../hooks/useCategories';
import simcafeLogo from '../assets/SIMcafe.svg';
import growPLMLogo from '../assets/Grow@PLM.png';
import './SessionCard.css';

/**
 * SessionCard Component
 * Session card matching mockUI.html design exactly
 * Feature: Phase 5 US4 - T111: Display presenter pictures with lazy loading
 */
const SEGMENT_DISPLAY_NAMES = {
  GROW_PLM: 'Grow@PLM',
  OPS_SW: 'OPS-SW',
  LCS: 'LCS',
  ME: 'ME',
  PES: 'PES',
  SIM: 'SIM',
};

const SessionCard = ({ session, timezone, isArchived, lightweightMode, isGrowPLM, showSegmentPrefix }) => {
  const [convertedTime, setConvertedTime] = useState(session.timeCET);
  const { byCode: categoryColors } = useCategories();

  const buttonColor = (categoryColors[session.category]?.color) || '#005f99';

  useEffect(() => {
    if (timezone && timezone !== 'cet') {
      try {
        const { start: startCET, end: endCET } = getSessionInterval(session.date, session.timeCET);
        const targetZone = timezone === 'local' ? 'local'
          : ['CET', 'Central Europe'].includes(timezone) ? 'Europe/Paris' : timezone;
        const startConverted = startCET.setZone(targetZone);
        const endConverted = endCET.setZone(targetZone);
        if (!startConverted.isValid || !endConverted.isValid) {
          setConvertedTime(session.timeCET);
          return;
        }

        const converted = `${startConverted.toFormat('HH:mm')}-${endConverted.toFormat('HH:mm')}`;
        setConvertedTime(converted);
      } catch (error) {
        console.error('Timezone conversion error:', error);
        setConvertedTime(session.timeCET);
      }
    } else {
      setConvertedTime(session.timeCET);
    }
  }, [session, timezone]);

  const handleLinkClick = (e) => {
    const btnText = session.buttonText || (isArchived ? 'View Recording' : 'Save Your Seat');
    // "Coming Soon" - no link at all
    if (btnText === 'Coming Soon') {
      e.preventDefault();
      return;
    }
    // "Postponed" - link disabled for end users
    if (btnText === 'Postponed') {
      e.preventDefault();
      return;
    }
    if (!session.sabaLink || !/^https?:\/\//i.test(session.sabaLink)) {
      e.preventDefault();
      alert('Session link is not available');
    }
  };

  // Determine button text and style
  const displayButtonText = session.buttonText || (isArchived ? 'View Recording' : 'Save Your Seat');
  const isButtonDisabled = /^(postponed|coming soon)$/i.test(displayButtonText);
  // Treat non-URL sabaLink values (e.g. "Coming soon") as no link
  const sabaLink = session.sabaLink && /^https?:\/\//i.test(session.sabaLink) ? session.sabaLink : null;

  return (
    <div 
      className="card" 
    >
      <a
        href={isButtonDisabled ? undefined : (sabaLink || '#')}
        className={`btn${isButtonDisabled ? ' btn-disabled' : ''}`}
        style={{ 
          backgroundColor: isArchived ? '#a52a2a' : buttonColor,
          opacity: isButtonDisabled ? 0.6 : 1,
          cursor: isButtonDisabled ? 'default' : 'pointer'
        }}
        target={isButtonDisabled ? undefined : '_blank'}
        rel="noopener noreferrer"
        onClick={isButtonDisabled ? (e) => e.preventDefault() : handleLinkClick}
        tabIndex={isButtonDisabled ? -1 : 0}
        aria-disabled={isButtonDisabled || undefined}
      >
        {displayButtonText}
      </a>

      <div className="session-time">
        {convertedTime} ({timezone === 'local' ? 'Local Time' : timezone})
      </div>

      {/* Phase 5 US4 - T111: Presenter picture with lazy loading - Support multiple pictures */}
      <div className="avatar-row">
        {/* Grow@PLM badge - shown to the left of presenter pictures */}
        {isGrowPLM && (
          <div className="avatar growplm-badge" title="Grow@PLM Session">
            <img src={growPLMLogo} alt="Grow@PLM" className="presenter-picture growplm-logo" />
          </div>
        )}
        {/simcaf/i.test(session.name || '') ? (
          <div className="avatar simcafe-avatar" title="SIMcafé">
            <img src={simcafeLogo} alt="SIMcafé" className="presenter-picture simcafe-logo" />
          </div>
        ) : !lightweightMode && session.presenterPictureUrl ? (() => {
          // Parse picture URLs (can be single URL or JSON array)
          let pictureUrls = [];
          try {
            pictureUrls = JSON.parse(session.presenterPictureUrl);
            if (!Array.isArray(pictureUrls)) {
              pictureUrls = [session.presenterPictureUrl];
            }
          } catch {
            pictureUrls = [session.presenterPictureUrl];
          }

          // Comma is always the separator between presenters
          const presenters = (session.presenter || '').split(',').map(p => p.trim()).filter(p => p);

          // Ensure we have a slot for every presenter
          while (pictureUrls.length < presenters.length) {
            pictureUrls.push(null);
          }

          return pictureUrls.map((url, index) => {
            const presenterName = presenters[index] || session.presenter;

            if (url) {
              return (
                <div key={index} className="avatar" title={presenterName}>
                  <LazyImage
                    src={url.startsWith('http') ? url : `${url}`}
                    alt={presenterName}
                    className="presenter-picture"
                    fallback={
                      <div className="presenter-placeholder" title={presenterName}>
                        👤
                      </div>
                    }
                  />
                </div>
              );
            }
            return (
              <div key={index} className="avatar" title={presenterName}>
                <div className="presenter-placeholder">
                  👤
                </div>
              </div>
            );
          });
        })() : (
          <div className="avatar">
            <div className="presenter-placeholder">
              👤
            </div>
          </div>
        )}
      </div>

      <h3>{showSegmentPrefix && session.segment ? `${SEGMENT_DISPLAY_NAMES[session.segment] || session.segment} - ${session.name}` : session.name}</h3>

      {session.presenter && (
        <div className="author">{session.presenter}</div>
      )}

      {session.guestSpeaker === 'Yes' && (
        <div className="guest-speaker-badge">🎤 Guest Speaker</div>
      )}

      {session.description && (
        <p className="description">{session.description}</p>
      )}
    </div>
  );
};

export default SessionCard;
